/**
 * @fileoverview Organization settings service for reporting currency.
 * @module features/settings/api/orgSettingsService
 */

import apiClient from '@/lib/apiClient'
import { z } from 'zod'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'

/** ISO-4217 currency code (3 uppercase letters). */
export const CurrencyCodeSchema = z
  .string()
  .length(3, 'Currency code must be 3 letters')
  .regex(/^[A-Z]{3}$/)

/**
 * Normalized response used across the app.
 * Note: Backend may return `allowedCurrencies` (legacy) or `allowedReportingCurrencies`.
 */
export const OrgSettingsResponseSchema = z.object({
  reportingCurrency: CurrencyCodeSchema,
  allowedReportingCurrencies: z.array(CurrencyCodeSchema).min(1),
})
export type OrgSettingsResponse = z.infer<typeof OrgSettingsResponseSchema>

/** Server shapes we accept (legacy + current) */
const OrgSettingsServerResponseLegacySchema = z.object({
  reportingCurrency: CurrencyCodeSchema,
  allowedCurrencies: z.array(CurrencyCodeSchema).min(1),
})
const OrgSettingsServerResponseCurrentSchema = z.object({
  reportingCurrency: CurrencyCodeSchema,
  allowedReportingCurrencies: z.array(CurrencyCodeSchema).min(1),
})
const OrgSettingsServerResponseUnion = z.union([
  OrgSettingsServerResponseCurrentSchema,
  OrgSettingsServerResponseLegacySchema,
])
type OrgSettingsServerResponse = z.infer<typeof OrgSettingsServerResponseUnion>

function normalizeOrgSettings(input: OrgSettingsServerResponse): OrgSettingsResponse {
  // Normalize legacy `allowedCurrencies` to `allowedReportingCurrencies`
  const allowed =
    'allowedReportingCurrencies' in input
      ? input.allowedReportingCurrencies
      : input.allowedCurrencies
  return {
    reportingCurrency: input.reportingCurrency,
    allowedReportingCurrencies: allowed,
  }
}

/** Request body for PATCH /api/orgs/{orgId}/settings */
export const UpdateOrgSettingsRequestSchema = z.object({
  reportingCurrency: CurrencyCodeSchema,
})
export type UpdateOrgSettingsRequest = z.infer<typeof UpdateOrgSettingsRequestSchema>

/**
 * Fetch organization settings (reporting currency + allowed values)
 * @param orgId Organization ID
 */
export async function getOrgSettings(
  orgId: z.infer<typeof OrgIdSchema>
): Promise<OrgSettingsResponse> {
  const id = OrgIdSchema.parse(orgId)
  const res = await apiClient.get(`/orgs/${id}/settings`)
  const raw = OrgSettingsServerResponseUnion.parse(res.data)
  return normalizeOrgSettings(raw)
}

/**
 * Update reporting currency for the org.
 * @param orgId Organization ID
 * @param body Update body containing reportingCurrency
 */
export async function updateOrgSettings(
  orgId: z.infer<typeof OrgIdSchema>,
  body: UpdateOrgSettingsRequest
): Promise<OrgSettingsResponse> {
  const id = OrgIdSchema.parse(orgId)
  const safe = UpdateOrgSettingsRequestSchema.parse(body)
  const res = await apiClient.patch(`/orgs/${id}/settings`, safe)
  const raw = OrgSettingsServerResponseUnion.parse(res.data)
  return normalizeOrgSettings(raw)
}
